import { faArrowRight, faTag } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { usePortalAuth } from '../../../hooks/usePortalAuth';
import { getStorefrontItemsByCategory } from '../../../services/portalDashboardService';
import { ItemCarousel } from './ItemCarousel';

/**
 * Items in a single category — "Top in Sauces" / "Top in Beverages"
 * style carousel curated by the admin via `widgetConfig.category`.
 * Anonymous-safe.
 *
 * The header doubles as a deep-link to the catalog scoped to that
 * category, mirroring the "Browse catalog" affordance on Popular Right
 * Now. Self-hides when the category is unset or has no shoppable items
 * for this brand so the row collapses cleanly.
 */
export function ItemsByCategoryWidget({
	title,
	category,
	limit = 12,
	density,
}: {
	title?: string;
	category: string | null;
	limit?: number;
	density?: 'standard' | 'compact';
}): JSX.Element | null {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { token } = usePortalAuth();

	const queryKey = [
		'portal',
		'dashboard',
		'items-by-category',
		category,
		limit,
	];
	const { data, isLoading, isError } = useQuery({
		queryKey,
		queryFn: () =>
			getStorefrontItemsByCategory(token, category as string, limit),
		enabled: category != null && category.trim().length > 0,
		staleTime: 5 * 60_000,
	});

	if (category == null || category.trim().length === 0) return null;

	const items = data?.data ?? [];
	if (!isLoading && !isError && items.length === 0) return null;

	const heading = title ?? `${t('Top in')} ${t(category)}`;

	return (
		<section className="w-full h-full flex flex-col">
			<header className="flex items-center justify-between mb-3">
				<h2 className="flex items-center gap-2 text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100">
					<FontAwesomeIcon icon={faTag} className="text-portal-accent" />
					{t(heading)}
				</h2>
				<button
					type="button"
					onClick={() =>
						navigate(`/catalog?category=${encodeURIComponent(category)}`)
					}
					className="text-xs md:text-sm text-portal-accent hover:opacity-80 inline-flex items-center gap-1"
				>
					{t('Browse all')}
					<FontAwesomeIcon icon={faArrowRight} className="text-[10px]" />
				</button>
			</header>

			<ItemCarousel
				items={items}
				invalidateQueryKey={queryKey}
				density={density}
				emptyState={null}
			/>
		</section>
	);
}
